import { supabase } from './supabaseClient';
import { RecentUpload } from '../types';
import { format, startOfMonth } from 'date-fns';

const getUserScans = async (userId: string): Promise<RecentUpload[]> => {
  const { data, error } = await supabase.functions.invoke('get-user-scans', {
    body: { userId },
  });

  if (error) {
    console.error('Error fetching user scans:', error);
    throw error;
  }

  if (!data || !data.scans) {
    return [];
  }

  return data.scans.map((scan: any) => ({
    id: scan.id,
    filename: scan.image_url || 'No Image',
    uploader_name: '', // This will be populated by the caller
    uploader_id: scan.user_id,
    upload_time: scan.created_at,
    file_size: 0, // Not available here
    status: scan.status || 'complete',
    api_provider: scan.api_provider,
    result: scan.result,
    title: scan.title || 'Untitled Scan',
  }));
};

const getUserStats = async (userId: string): Promise<ScanStats> => {
  try {
    // Get current user to ensure authentication context
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      console.error('User not authenticated:', userError);
      throw new Error('User not authenticated');
    }

    // Verify the user ID matches the authenticated user
    if (user.id !== userId) {
      throw new Error('User ID mismatch - unauthorized access');
    }

    // Total Scans - Use count without head option
    const { count: totalScans, error: totalScansError } = await supabase
      .from('scans')
      .select('*', { count: 'exact' })
      .eq('user_id', userId);

    if (totalScansError) throw totalScansError;

    // Saved Items 
    const { count: savedItems, error: savedItemsError } = await supabase
      .from('scans')
      .select('*', { count: 'exact' })
      .eq('user_id', userId)
      .eq('is_saved', true);

    if (savedItemsError) throw savedItemsError;

    // Scans This Month
    const startOfCurrentMonth = format(startOfMonth(new Date()), 'yyyy-MM-dd HH:mm:ss');
    const { count: thisMonth, error: thisMonthError } = await supabase
      .from('scans')
      .select('*', { count: 'exact' })
      .eq('user_id', userId)
      .gte('created_at', startOfCurrentMonth);

    if (thisMonthError) throw thisMonthError;

    const stats = {
      totalScans: totalScans || 0,
      savedItems: savedItems || 0,
      thisMonth: thisMonth || 0,
    };

    console.log('Fetched user stats:', stats);
    return stats;
  } catch (error) {
    console.error('Error fetching user stats:', error);
    throw error;
  }
};

export const supabaseService = {
  getUserScans,
  getUserStats,
};

export interface ScanStats {
  totalScans: number;
  savedItems: number;
  thisMonth: number;
}
